import { useState, useEffect } from 'react';
import moment from 'moment';
import apiService from '../services/api';
import { API_CONFIG } from '../config/api';

/**
 * Lightweight hook to fetch today\'s EYA daily summary once and expose it.
 * Currently leverages the same getForEnergyLoss endpoint that already returns
 * an EYA block with daily KPIs (generation times, exported energy breakdown,
 * loss percentages, etc.).
 * The result is cached in-memory for 5 minutes to prevent redundant calls.
 */

const CACHE = { ts: 0, data: null };
const TTL_MS = 5 * 60 * 1000; // 5-minutes

const FALLBACK = {
  generation_start_time: '05:10',
  generation_end_time: '18:25',
  total_generation_time: '13:15',
  total_hrs_gpoa_gt_100: '12:00',
  total_hrs_gpoa_gt_500: '10:10',
  peak_maximum_power: '151.01',
  peak_maximum_poa: '1018.38',
  poi1_exported_energy: '1509.4',
  poi2_exported_energy: '1487.4',
  total_exported_energy: '2996.8',
  total_imported_energy: '12.6',
  inverter_availability: '100',
  soiling_loss: '0.04',
  inverter_breakdown_loss: '0.01',
  module_temperature_loss: '4.52',
  total_energy_loss: '20.94'
};

export default function useDailySummary() {
  const [summary, setSummary] = useState(CACHE.data);
  const [loading, setLoading] = useState(!CACHE.data);
  const [error, setError] = useState(null);

  useEffect(() => {
    const now = Date.now();
    if (CACHE.data && now - CACHE.ts < TTL_MS) {
      setLoading(false);
      return; // still fresh
    }

    const fetchSummary = async () => {
      try {
        setLoading(true);
        const today = moment().format('YYYY-MM-DD');
        const res = await apiService.getForEnergyLoss({
          start_date: `${today} 00:00:00`,
          end_date: `${today} 23:55:00`,
          plant_id: API_CONFIG.PLANT_ID,
          asset_id_list: [API_CONFIG.PLANT_ID]
        });

        const arr = Array.isArray(res.data) ? res.data : (res.data?.body || []);
        const eya = arr[0]?.readings?.[0]?.EYA || {};

        // Attempt to enrich with getForGraph (for peak power/POA)
        try {
          const graphRes = await apiService.getForGraph({
            start_date: `${today} 00:00:00`,
            end_date: `${today} 23:55:00`,
            plant_id: API_CONFIG.PLANT_ID,
            asset_id_list: [API_CONFIG.PLANT_ID]
          });
          eya.graph = graphRes.data;
        } catch(_){}

        // Attempt PVSyst fetch for losses
        try {
          const pvsRes = await apiService.getPvSystData({
            date: today,
            plant_id: API_CONFIG.PLANT_ID
          });
          eya.pvsyst = pvsRes.data;
        } catch(_){}

        const merged = { ...FALLBACK, ...eya };
        CACHE.data = merged;
        CACHE.ts = Date.now();
        setSummary(merged);
      } catch (err) {
        console.error('Daily summary fetch error', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, []);

  return { summary, loading, error };
}
